import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { formatDistanceToNow, parse } from "date-fns";
import { Pressable } from "react-native";
import { useFragment } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import { ArrowRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
  Center,
} from "@whoosh/design-system/src/components/primitive";

import { PreviousBookingsListItem$key } from "./__generated__/PreviousBookingsListItem.graphql";

type PreviousBookingsListItemProps = {
  bookingRef: PreviousBookingsListItem$key | null;
  currentPatronId: string;
  onPress: (bookingId: string, durationMins?: number | null) => void;
};

export const PreviousBookingsListItem = ({
  bookingRef,
  currentPatronId,
  onPress,
}: PreviousBookingsListItemProps) => {
  const data = useFragment(PreviousBookingsListItemFragmentString, bookingRef);

  const newDate = parse(
    `${data?.startSlot?.agenda?.date} ${data?.startSlot?.time}`,
    "yyyy-MM-dd HH:mm:ss",
    new Date()
  );
  const timeAgo = formatDistanceToNow(newDate);

  const currentPatronRound = data?.rounds?.edges?.find(
    (edge) => edge?.node?.player?.id === currentPatronId
  );

  const holeCountString = currentPatronRound?.node?.courseLayout
    ? currentPatronRound?.node?.courseLayout?.holeCount === "EIGHTEEN"
      ? t`18 Holes`
      : t`9 Holes`
    : null;

  const otherPlayers = data?.rounds?.edges?.filter(
    (edge) => edge?.node?.player?.id !== currentPatronId
  );

  const supportsPrivate =
    data?.startSlot?.agenda.facility.club?.supportsPrivate;

  const otherPlayerNames = otherPlayers?.map((edge) =>
    edge?.node?.player?.fullName
      ? edge?.node?.player?.fullName
      : supportsPrivate
      ? t`Unknown Guest`
      : t`Unknown Customer`
  );

  const playersString =
    otherPlayers?.length === 0
      ? t`You`
      : otherPlayerNames?.length === 1
      ? t`You and ${otherPlayerNames[0]}`
      : t`You, ${otherPlayerNames?.join(", ") ?? ""}`;

  // Determine if we need to show the "+x" indicator
  const roundEdges = data?.rounds?.edges || [];
  const MAX_VISIBLE_AVATARS = 7;
  const shouldShowCounter = roundEdges.length > 8;
  const visibleAvatars = shouldShowCounter
    ? roundEdges.slice(0, MAX_VISIBLE_AVATARS)
    : roundEdges;
  const remainingCount = shouldShowCounter
    ? roundEdges.length - MAX_VISIBLE_AVATARS
    : 0;

  return (
    <Pressable
      onPress={() => onPress(data?.id ?? "", data?.durationMins)}
      testID={`previous-booking-${data?.id}`}
      accessibilityRole="button"
      accessibilityLabel={t`Rebook previous booking`}
    >
      <Box>
        <Box position="relative" p={5} pb={0} zIndex={10}>
          <HStack style={{ marginBottom: -20 }} zIndex={10}>
            {visibleAvatars.map((edge, index) => (
              <Box
                key={edge?.node?.player?.id}
                borderWidth={2}
                borderColor="surface-primary"
                borderRadius="2"
                overflow="hidden"
                style={{
                  marginLeft: index === 0 ? 0 : -4,
                }}
              >
                <Avatar
                  key={edge?.node?.player?.id}
                  profileImagePublicId={
                    edge?.node?.player?.profileImage?.publicId
                  }
                  firstName={edge?.node?.player?.firstName}
                  lastName={edge?.node?.player?.lastName}
                  size="small"
                />
              </Box>
            ))}
            {shouldShowCounter ? (
              <Box
                borderWidth={2}
                borderColor="surface-primary"
                bg="surface-main-primary-subtlest"
                borderRadius="2"
                overflow="hidden"
                style={{
                  marginLeft: -4,
                }}
                width={44}
                height={44}
              >
                <Center flex={1}>
                  <Text variant="mainSmallSemiBold">+{remainingCount}</Text>
                </Center>
              </Box>
            ) : null}
          </HStack>
        </Box>

        <HStack
          justifyContent="space-between"
          alignItems="center"
          p={5}
          bg="surface-primary"
          pt={6}
          gap={4}
        >
          <VStack width="100%" flexShrink={1}>
            <Text
              variant="mainSmallSemiBold"
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {playersString}
            </Text>
            <Text variant="mainSmallMedium" color="text-secondary">
              {timeAgo} {t`ago`}{" "}
              {holeCountString ? ` · ${holeCountString}` : ""}
            </Text>
          </VStack>
          <ArrowRightIcon />
        </HStack>
      </Box>
    </Pressable>
  );
};

export const PreviousBookingsListItemFragmentString = graphql`
  fragment PreviousBookingsListItem on StandardBooking {
    id
    durationMins
    rounds {
      edges {
        node {
          courseLayout {
            code
            name
            holeCount
          }
          player {
            id
            fullName
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
    startSlot {
      type
      agenda {
        date
        facility {
          club {
            supportsPrivate
          }
        }
      }
      ... on TimeSlot {
        time
      }
    }
  }
`;
